import { createFileRoute } from "@tanstack/react-router";
import { useSiteData } from "@/lib/use-site-data";

export const Route = createFileRoute("/legal/privacidad")({
  head: () => ({
    meta: [
      { title: "Política de privacidad · El Pajar de Benito" },
      { name: "description", content: "Cómo tratamos tus datos personales conforme al RGPD y la LOPDGDD en elpajardebenito.es." },
      { property: "og:title", content: "Política de privacidad · El Pajar de Benito" },
      { property: "og:description", content: "Información sobre el tratamiento de datos personales, base jurídica, plazos y derechos RGPD." },
      { property: "og:url", content: "https://elpajardebenito.es/legal/privacidad" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://elpajardebenito.es/legal/privacidad" }],
  }),
  component: Privacidad,
});

function Privacidad() {
  const { config } = useSiteData();
  const hoy = new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });
  const titular = config.nombre_fiscal || "CASA JUANA Y LUCIO S.L.";
  return (
    <>
      <h1>Política de privacidad</h1>
      <p className="text-sm text-muted-foreground">Última actualización: {hoy}</p>

      <p>La presente política de privacidad regula el tratamiento de los datos personales que facilitas a través del sitio web elpajardebenito.es, conforme al Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018, de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD).</p>

      <h2>1. Responsable del tratamiento</h2>
      <ul>
        <li><strong>Responsable:</strong> {titular}</li>
        <li><strong>NIF:</strong> {config.nif || "B85745362"}</li>
        <li><strong>Dirección:</strong> {config.direccion}, {config.codigo_postal} {config.ciudad}, {config.pais}</li>
        <li><strong>Email de contacto:</strong> <a href={`mailto:${config.email_contacto}`}>{config.email_contacto}</a></li>
      </ul>

      <h2>2. Finalidades del tratamiento</h2>
      <p>Tratamos tus datos personales para las siguientes finalidades:</p>
      <ul>
        <li><strong>Gestión de reservas:</strong> tramitar, confirmar y gestionar las reservas de alojamiento realizadas a través del sitio web, incluyendo comunicaciones relativas a la estancia.</li>
        <li><strong>Facturación y obligaciones fiscales:</strong> emisión de facturas y cumplimiento de las obligaciones contables, fiscales y administrativas.</li>
        <li><strong>Atención al cliente:</strong> responder a las consultas realizadas a través del formulario de contacto, email o WhatsApp.</li>
        <li><strong>Comunicaciones transaccionales:</strong> envío de emails imprescindibles ligados a la reserva (confirmación, recordatorio de llegada, información de check-in, recibo de pago).</li>
        <li><strong>Registro de viajeros:</strong> comunicación de datos de los huéspedes a las autoridades competentes, conforme a la normativa vigente sobre registro documental de viajeros en establecimientos de hospedaje.</li>
      </ul>

      <h2>3. Base jurídica</h2>
      <ul>
        <li><strong>Ejecución de un contrato</strong> (art. 6.1.b RGPD): gestión de la reserva y prestación del servicio de alojamiento.</li>
        <li><strong>Cumplimiento de una obligación legal</strong> (art. 6.1.c RGPD): facturación, contabilidad y registro obligatorio de viajeros.</li>
        <li><strong>Consentimiento del interesado</strong> (art. 6.1.a RGPD): consultas a través del formulario de contacto y comunicaciones no imprescindibles.</li>
        <li><strong>Interés legítimo</strong> (art. 6.1.f RGPD): mantenimiento de la relación comercial y prevención del fraude.</li>
      </ul>

      <h2>4. Plazos de conservación</h2>
      <ul>
        <li>Datos de reservas y facturación: durante el tiempo necesario para la prestación del servicio y, posteriormente, durante los plazos legales aplicables (mínimo 6 años conforme al Código de Comercio y 4 años a efectos fiscales).</li>
        <li>Registro de viajeros: durante el plazo exigido por la normativa aplicable.</li>
        <li>Datos de consultas: hasta la resolución de la consulta y, salvo oposición, hasta un máximo de 12 meses.</li>
      </ul>

      <h2>5. Destinatarios y encargados del tratamiento</h2>
      <p>Tus datos podrán ser comunicados a las siguientes categorías de destinatarios, siempre bajo garantías adecuadas y contratos de encargo de tratamiento (art. 28 RGPD):</p>
      <ul>
        <li><strong>Proveedor de pago:</strong> Redsys Servicios de Procesamiento, S.L. (pasarela de pago con tarjeta). Los datos de la tarjeta se introducen directamente en el entorno seguro del banco; el titular del sitio no los almacena.</li>
        <li><strong>Proveedor de email transaccional:</strong> Resend (Resend, Inc., EE. UU.) para el envío de emails de confirmación de reserva, acogido a marcos válidos de transferencia internacional.</li>
        <li><strong>Proveedor de alojamiento web:</strong> infraestructura cloud utilizada para el hospedaje del sitio y la base de datos.</li>
        <li><strong>Administraciones Públicas:</strong> cuando exista obligación legal (registro de viajeros, requerimientos de la Agencia Tributaria, etc.).</li>
        <li><strong>Asesoría fiscal y contable:</strong> para el cumplimiento de las obligaciones contables y tributarias.</li>
      </ul>

      <h2>6. Transferencias internacionales</h2>
      <p>Algunos proveedores (como Resend) están ubicados fuera del Espacio Económico Europeo. Estas transferencias se realizan al amparo de garantías adecuadas: cláusulas contractuales tipo aprobadas por la Comisión Europea u otros mecanismos previstos en el Capítulo V del RGPD.</p>

      <h2>7. Derechos de las personas interesadas</h2>
      <p>Puedes ejercer en cualquier momento los siguientes derechos:</p>
      <ul>
        <li><strong>Acceso:</strong> saber qué datos tuyos tratamos.</li>
        <li><strong>Rectificación:</strong> corregir datos inexactos.</li>
        <li><strong>Supresión (derecho al olvido):</strong> solicitar la eliminación de tus datos cuando ya no sean necesarios.</li>
        <li><strong>Oposición:</strong> oponerte a un tratamiento concreto.</li>
        <li><strong>Limitación:</strong> solicitar la limitación del tratamiento en los supuestos previstos.</li>
        <li><strong>Portabilidad:</strong> recibir tus datos en un formato estructurado.</li>
        <li><strong>Retirada del consentimiento</strong> en cualquier momento, sin efectos retroactivos.</li>
      </ul>
      <p>Para ejercer estos derechos, envía una solicitud por escrito a <a href={`mailto:${config.email_contacto}`}>{config.email_contacto}</a> o al domicilio del responsable, adjuntando copia de tu DNI o documento equivalente. También puedes presentar una reclamación ante la <strong>Agencia Española de Protección de Datos</strong> (<a href="https://www.aepd.es" target="_blank" rel="noopener">www.aepd.es</a>) cuando consideres que el tratamiento no se ajusta a la normativa.</p>

      <h2>8. Seguridad</h2>
      <p>Hemos adoptado las medidas técnicas y organizativas apropiadas para garantizar un nivel de seguridad adecuado al riesgo, conforme al artículo 32 del RGPD, incluyendo cifrado en tránsito (HTTPS), control de accesos y copias de seguridad.</p>

      <h2>9. Menores de edad</h2>
      <p>El sitio web no está dirigido a menores de 14 años. No recogemos deliberadamente datos personales de menores; si detectamos que se han facilitado, procederemos a su supresión inmediata.</p>

      <h2>10. Modificaciones</h2>
      <p>El responsable se reserva el derecho a modificar la presente política para adaptarla a novedades legislativas o jurisprudenciales. Se recomienda revisar esta página periódicamente.</p>
    </>
  );
}